import { Backdrop, Button, Card, Eyebrow, Input, ThemeToggle } from '@memorizecards/ui';
import type { FormEvent, ReactNode } from 'react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../state/auth';

const SENHA_MINIMA = 10;

function validarNovaSenha(senha: string): string | null {
  if (senha.length < SENHA_MINIMA) {
    return `A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`;
  }
  if (!/[a-z]/.test(senha)) return 'Inclua pelo menos uma letra minúscula.';
  if (!/[A-Z]/.test(senha)) return 'Inclua pelo menos uma letra maiúscula.';
  if (!/[0-9]/.test(senha)) return 'Inclua pelo menos um número.';
  return null;
}

function Moldura({
  titulo,
  eyebrow,
  children,
}: {
  titulo: string;
  eyebrow: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="relative grid min-h-screen place-items-center px-4 py-12">
      <Backdrop />
      <div className="fixed right-4 top-4 z-50">
        <ThemeToggle size="sm" />
      </div>
      <div className="relative z-[2] w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <Logo />
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1 className="text-mc-h3">{titulo}</h1>
        </div>
        <Card padding="lg">{children}</Card>
      </div>
    </div>
  );
}

function Aviso({ tom, children }: { tom: 'erro' | 'ok'; children: ReactNode }): ReactNode {
  return (
    <p
      className={`rounded-mc-field border px-4 py-3 text-sm text-mc-ink ${
        tom === 'erro'
          ? 'border-mc-danger/40 bg-mc-danger/10'
          : 'border-mc-accent/40 bg-mc-accent/10'
      }`}
      role={tom === 'erro' ? 'alert' : 'status'}
    >
      {children}
    </p>
  );
}

// ── Esqueci a senha ─────────────────────────────────────────────────────────

export function EsqueciSenha(): ReactNode {
  const [email, setEmail] = useState('');
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function aoSubmeter(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const res = await api<{ mensagem: string }>('/auth/forgot-password', {
        method: 'POST',
        body: { email: email.trim() },
      });
      setMensagem(res.mensagem);
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não consegui enviar agora.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Moldura eyebrow="Recuperação" titulo="Esqueceu a senha?">
      {mensagem ? (
        <div className="flex flex-col gap-5">
          <Aviso tom="ok">{mensagem}</Aviso>
          <p className="text-sm text-mc-ink2">
            O link vale por uma hora. Se não chegar em alguns minutos, procure na caixa
            de spam.
          </p>
          <Link to="/entrar">
            <Button variant="ghost" fullWidth>
              Voltar para o login
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={aoSubmeter} className="flex flex-col gap-5" noValidate>
          <p className="text-sm text-mc-ink2">
            Escreva o e-mail da sua conta. Enviamos um link para criar uma senha nova.
          </p>
          <Input
            label="E-mail"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@exemplo.com"
          />
          {erro ? <Aviso tom="erro">{erro}</Aviso> : null}
          <Button type="submit" size="lg" fullWidth loading={enviando}>
            Enviar link
          </Button>
          <Link
            to="/entrar"
            className="text-center text-sm text-mc-ink3 underline underline-offset-4 hover:text-mc-accent"
          >
            Voltar
          </Link>
        </form>
      )}
    </Moldura>
  );
}

// ── Redefinir com token ─────────────────────────────────────────────────────

export function RedefinirSenha(): ReactNode {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);

  async function aoSubmeter(e: FormEvent) {
    e.preventDefault();
    setErro(null);

    if (senha.length < SENHA_MINIMA) {
      setErro(`A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`);
      return;
    }
    const regra = validarNovaSenha(senha);
    if (regra) {
      setErro(regra);
      return;
    }
    if (senha !== confirmacao) {
      setErro('As duas senhas não são iguais.');
      return;
    }

    setEnviando(true);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: { token, novaSenha: senha, confirmar: confirmacao },
      });
      setPronto(true);
      window.setTimeout(() => navigate('/entrar', { replace: true }), 2500);
    } catch (error) {
      setErro(
        error instanceof ApiError
          ? error.message
          : 'Este link não é mais válido. Peça outro.',
      );
    } finally {
      setEnviando(false);
    }
  }

  if (!token) {
    return (
      <Moldura eyebrow="Recuperação" titulo="Link inválido">
        <div className="flex flex-col gap-5">
          <Aviso tom="erro">
            Este endereço não tem o código de recuperação. Peça um link novo.
          </Aviso>
          <Link to="/esqueci-senha">
            <Button fullWidth>Pedir novo link</Button>
          </Link>
        </div>
      </Moldura>
    );
  }

  return (
    <Moldura eyebrow="Recuperação" titulo="Criar senha nova">
      {pronto ? (
        <div className="flex flex-col gap-5">
          <Aviso tom="ok">Senha alterada. Levando você para o login…</Aviso>
          <Link to="/entrar">
            <Button fullWidth>Ir agora</Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={aoSubmeter} className="flex flex-col gap-5" noValidate>
          <Input
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            required
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            hint="Mínimo de 10 caracteres, com maiúscula, minúscula e número."
          />
          <Input
            label="Repita a nova senha"
            type="password"
            autoComplete="new-password"
            required
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
          />
          {erro ? <Aviso tom="erro">{erro}</Aviso> : null}
          <Button type="submit" size="lg" fullWidth loading={enviando}>
            Salvar senha
          </Button>
        </form>
      )}
    </Moldura>
  );
}

// ── Troca obrigatória no primeiro acesso ────────────────────────────────────

/**
 * Quem recebe uma senha temporária — conta criada pelo pagamento ou pelo
 * superadmin — não navega para lado nenhum antes de a trocar. A rota é
 * imposta no `RotaProtegida`; aqui só se trata do formulário.
 */
export function TrocarSenha(): ReactNode {
  const { user, sair } = useAuth();
  const navigate = useNavigate();

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);

  async function aoSubmeter(e: FormEvent) {
    e.preventDefault();
    setErro(null);

    if (novaSenha !== confirmacao) {
      setErro('As duas senhas não são iguais.');
      return;
    }
    const regra = validarNovaSenha(novaSenha);
    if (regra) {
      setErro(regra);
      return;
    }

    setEnviando(true);
    try {
      await api('/auth/change-password', {
        method: 'POST',
        body: { senhaAtual, novaSenha, confirmar: confirmacao },
      });
      setPronto(true);
      // A API derruba todas as sessões, incluindo esta — é preciso entrar de novo.
      await sair();
      window.setTimeout(() => navigate('/entrar', { replace: true }), 2000);
    } catch (error) {
      setErro(
        error instanceof ApiError ? error.message : 'Não consegui trocar a senha.',
      );
      setEnviando(false);
    }
  }

  return (
    <Moldura
      eyebrow="Primeiro acesso"
      titulo={user?.mustChangePassword ? 'Escolha sua senha' : 'Trocar senha'}
    >
      {pronto ? (
        <Aviso tom="ok">Senha alterada. Entre de novo com a senha nova.</Aviso>
      ) : (
        <form onSubmit={aoSubmeter} className="flex flex-col gap-5" noValidate>
          {user?.mustChangePassword ? (
            <p className="text-sm text-mc-ink2">
              Sua conta foi criada com uma senha temporária. Escolha uma senha só sua
              para continuar.
            </p>
          ) : null}
          <Input
            label="Senha atual"
            type="password"
            autoComplete="current-password"
            required
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
          />
          <Input
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            required
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            hint="Mínimo de 10 caracteres, com maiúscula, minúscula e número."
          />
          <Input
            label="Repita a nova senha"
            type="password"
            autoComplete="new-password"
            required
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
          />
          {erro ? <Aviso tom="erro">{erro}</Aviso> : null}
          <Button type="submit" size="lg" fullWidth loading={enviando}>
            Salvar e entrar de novo
          </Button>
        </form>
      )}
    </Moldura>
  );
}
